/**
 * The `Invited` section's row: who was invited, who invited them and when,
 * plus a `Cancel invite` control that only shows up when `row.canCancel` is
 * true. `canCancel` renders no control at all when false, not a disabled
 * one: D4 says "sees no delete control on that row," and a disabled button
 * is still a control.
 */
import { Button, confirmDestructive } from "@sportsmet/ui";
import { type ReactNode } from "react";

import type { typeRosterInvitedRow } from "./membersRosterModel";

export type typePoolMemberInvitedRowProps = {
  row: typeRosterInvitedRow;
  isBusy: boolean;
  onCancel: (poolInviteId: string) => void;
};

export const CANCEL_INVITE_LABEL = "Cancel invite";
export const CANCEL_INVITE_CONFIRM_TITLE = "Cancel this invite?";
export const CANCEL_INVITE_CONFIRM_BODY =
  "They will not be able to join with this invite. You can invite them again later.";
export const CANCEL_INVITE_CONFIRM_LABEL = "Cancel invite";

export const PoolMemberInvitedRow = ({
  row,
  isBusy,
  onCancel,
}: typePoolMemberInvitedRowProps): ReactNode => {
  async function handleCancel(): Promise<void> {
    const isConfirmed = await confirmDestructive({
      title: CANCEL_INVITE_CONFIRM_TITLE,
      body: CANCEL_INVITE_CONFIRM_BODY,
      confirmLabel: CANCEL_INVITE_CONFIRM_LABEL,
    });
    if (!isConfirmed) return;
    onCancel(row.poolInviteId);
  }

  return (
    <li
      className="flex items-center justify-between gap-3"
      data-testid="pool-member-invited-row"
      data-invite-id={row.poolInviteId}
    >
      <div className="flex min-w-0 flex-1 flex-col">
        <span className="text-body-strong text-ink">{row.displayName}</span>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-caption text-ink-secondary">
            {row.invitedByLabel}
          </span>
          <span className="text-caption text-ink-secondary">
            {row.sentAtLabel}
          </span>
        </div>
      </div>
      {row.canCancel ? (
        <Button
          variant="destructive"
          size="md"
          data-testid="pool-member-invited-cancel"
          loading={isBusy}
          disabled={isBusy}
          onClick={() => {
            void handleCancel();
          }}
        >
          {CANCEL_INVITE_LABEL}
        </Button>
      ) : null}
    </li>
  );
};
